import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Detectar si ya está instalado
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    
    if (isInstalled) {
      localStorage.setItem('pwa-installed', 'true');
      return;
    }

    // Detectar plataforma
    const userAgent = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(userAgent);
    const android = /android/.test(userAgent);
    
    setIsIOS(iOS);
    setIsAndroid(android);

    // Verificar si ya se instaló o descartó
    const hasInstalled = localStorage.getItem('pwa-installed');
    const hasDismissed = localStorage.getItem('pwa-dismissed');
    const dismissedAt = localStorage.getItem('pwa-dismissed-at');
    
    // Si se descartó hace menos de 7 días, no mostrar
    if (hasDismissed && dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      } else {
        // Han pasado 7 días, limpiar el descarte
        localStorage.removeItem('pwa-dismissed');
        localStorage.removeItem('pwa-dismissed-at');
      }
    }

    if (hasInstalled) return;

    // Escuchar el evento beforeinstallprompt (solo Android Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Mostrar después de 3 segundos
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Si es iOS o Android pero no se disparó el evento, mostrar de todos modos
    if ((iOS || android) && !hasInstalled && !hasDismissed) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000); // Mostrar después de 5 segundos en iOS/Android
    }

    // Detectar cuando se instala
    window.addEventListener('appinstalled', () => {
      localStorage.setItem('pwa-installed', 'true');
      setShowPrompt(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    // Si hay prompt nativo de Chrome (Android), usarlo
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
    } else {
      // Si no hay prompt nativo, el botón no hace nada
      // El usuario debe usar el menú del navegador
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-dismissed', 'true');
    localStorage.setItem('pwa-dismissed-at', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fadeInUp">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-4 border border-white/10">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/70 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
            <Smartphone className="h-6 w-6 text-white" />
          </div>

          <div className="flex-1">
            <h3 className="text-white font-bold text-sm mb-1">
              Instalar TiendasYA
            </h3>
            <p className="text-white/80 text-xs mb-3">
              {isIOS 
                ? 'Toca el botón de compartir y luego "Añadir a pantalla de inicio"'
                : 'Accede más rápido y recibe notificaciones de pedidos'
              }
            </p>

            {/* Botón solo si hay prompt nativo (Android Chrome) */}
            {deferredPrompt && (
              <button
                onClick={handleInstall}
                className="w-full bg-white text-indigo-600 font-bold text-sm py-2 px-4 rounded-lg hover:bg-white/90 transition-colors"
              >
                Instalar App
              </button>
            )}

            {/* Instrucciones para iOS */}
            {isIOS && !deferredPrompt && (
              <div className="text-white/70 text-xs space-y-1">
                <p>1. Toca <span className="font-bold">⬆️ Compartir</span></p>
                <p>2. Selecciona <span className="font-bold">"Añadir a pantalla de inicio"</span></p>
              </div>
            )}

            {/* Instrucciones para Android sin prompt */}
            {isAndroid && !deferredPrompt && (
              <div className="text-white/70 text-xs space-y-1">
                <p>1. Toca el menú <span className="font-bold">⋮</span> (3 puntos)</p>
                <p>2. Selecciona <span className="font-bold">"Instalar app"</span></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
